// receipts/services/receiptsApi.js
//
// Same request pattern as Mpesa's api.js (authenticated fetch, 401 ->
// refresh -> retry once), scoped to the receipts endpoints added to
// mpesa/urls.py:
//
//   GET  /api/receipt-periods/               list_receipt_periods
//   GET  /api/receipt-periods/stats/         period_stats
//   GET  /api/receipt-periods/members/       period_member_receipts
//   GET  /api/receipt-periods/final-totals/  final_period_totals
//   GET  /api/receipts/                      list_receipts
//   GET  /api/receipts/<receipt_no>/         receipt_detail
//   GET  /api/receipts/<receipt_no>/print/   print_receipt (HTML, not JSON)
//   POST /api/receipts/change-period/        change_item_period
//
// NOTE ON PATHS: this file lives at receipts/services/receiptsApi.js —
// one level deeper than Mpesa's api.js (which lives at Mpesa/api.js and
// does `../config` / `../Auth/Access`). Adjust the two imports below if
// your actual folder depth differs from the assumption here (receipts/
// sitting alongside Mpesa/, both under src/).
import BASE_URL from '../../config';
import accessManager from '../../Auth/Access';

class ReceiptsApiService {
  constructor() {
    this.baseURL = BASE_URL;
  }

  async makeRequest(url, options = {}) {
    try {
      const token = accessManager.getTokens().access_token;

      const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      let response = await fetch(url, {
        headers,
        ...options,
      });

      if (response.status === 401 && token) {
        const refreshed = await accessManager.refreshTokenWithRetry();
        if (refreshed) {
          const newToken = accessManager.getTokens().access_token;
          headers.Authorization = `Bearer ${newToken}`;

          response = await fetch(url, {
            headers,
            ...options,
          });
        } else {
          accessManager.logout();
          throw new Error('Authentication failed - please login again');
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Receipts API request failed: ${url}`, error);
      throw error;
    }
  }

  // -------------------------------------------------------------------
  // Periods list — Receipts.js (page 1)
  // -------------------------------------------------------------------
  async fetchReceiptPeriods({ startDate, endDate, groupId, status, search } = {}) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (groupId) params.append('group_id', groupId);
    if (status) params.append('status', status);
    if (search) params.append('search', search);

    const response = await this.makeRequest(`${this.baseURL}/receipt-periods/?${params.toString()}`);
    return response.periods || [];
  }

  // -------------------------------------------------------------------
  // Period header — ReceiptDetail.js stat boxes
  // -------------------------------------------------------------------
  async fetchPeriodStats(groupId, periodEnd, type = 'regular') {
    const params = new URLSearchParams({ group_id: groupId, period_end: periodEnd, type });
    return this.makeRequest(`${this.baseURL}/receipt-periods/stats/?${params.toString()}`);
  }

  // -------------------------------------------------------------------
  // Member receipts table — ReceiptDetail.js (Mpesa/Cash tabs)
  // -------------------------------------------------------------------
  async fetchPeriodMemberReceipts(groupId, periodEnd, type = 'regular') {
    const params = new URLSearchParams({ group_id: groupId, period_end: periodEnd, type });
    const response = await this.makeRequest(`${this.baseURL}/receipt-periods/members/?${params.toString()}`);
    return response.receipts || [];
  }

  // -------------------------------------------------------------------
  // Final Period Receipt extras — PeriodReceiptDialog (variant='final')
  // -------------------------------------------------------------------
  async fetchFinalPeriodTotals(groupId, periodEnd, type = 'regular') {
    const params = new URLSearchParams({ group_id: groupId, period_end: periodEnd, type });
    return this.makeRequest(`${this.baseURL}/receipt-periods/final-totals/?${params.toString()}`);
  }

  // -------------------------------------------------------------------
  // Single receipt — MemberReceiptDialog's real transaction breakdown
  // -------------------------------------------------------------------
  async fetchReceipt(receiptNo) {
    const response = await this.makeRequest(`${this.baseURL}/receipts/${receiptNo}/`);
    return response.receipt;
  }

  async fetchReceipts({ memberId, groupId, periodEnd, dateFrom, dateTo } = {}) {
    const params = new URLSearchParams();
    if (memberId) params.append('member_id', memberId);
    if (groupId) params.append('group_id', groupId);
    if (periodEnd) params.append('period_end', periodEnd);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);

    const response = await this.makeRequest(`${this.baseURL}/receipts/?${params.toString()}`);
    return response.receipts || [];
  }

  // Printable HTML page — not JSON, so this returns the URL to open/print
  // rather than fetching it through makeRequest.
  getPrintReceiptUrl(receiptNo) {
    return `${this.baseURL}/receipts/${receiptNo}/print/`;
  }

  // -------------------------------------------------------------------
  // Period correction — kept here ready for when the Edit button (the
  // DistributeMpesaDialog flow) is wired up; not called from the UI yet.
  // -------------------------------------------------------------------
  async changeReceiptPeriod({ transactionId, newPeriodEnd, reason }) {
    return this.makeRequest(`${this.baseURL}/receipts/change-period/`, {
      method: 'POST',
      body: JSON.stringify({
        transaction_id: transactionId,
        new_period_end: newPeriodEnd,
        reason: reason || '',
      }),
    });
  }
}

const receiptsApi = new ReceiptsApiService();
export default receiptsApi;
export { ReceiptsApiService };